import {useAlbums, useDeleteAlbum} from '../utils/api';
import React, {useMemo, useState} from 'react';
import {Column, useTable} from 'react-table';
import {IAlbum} from '../interfaces/PlayerModels';
import {Button, ButtonGroup} from 'react-bootstrap';
import ConfirmModal from '@components/modals/ConfirmModal';
import EditAlbumModal from '@components/modals/EditAlbumModal';
import {useNavigate} from "react-router-dom";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";

type TableData = Omit<IAlbum, 'tracks'>;

const AlbumsTable = () => {
    const mutation = useDeleteAlbum();
    const {data} = useAlbums();
    const [itemToDelete, setItemToDelete] = useState('');
    const navigate = useNavigate();
    // Called if the user confirms the deletion
    const doDelete = async () => {
        const i = itemToDelete;
        setItemToDelete(''); // Clear the item we want to delete, so we can close the modal
        await mutation.mutateAsync({albumId: i});
        window.location.reload();

    };

    // Memoize this so that it isn't re-created on every re-render.
    const tableData = useMemo<TableData[]>(() => {
        if (!data || !data.data) return [];
        return data.data.map((p) => {
            return {
                id: p.id,
                title: p.title,
                image: p.image,
                artist: p.artist,
                platforms: p.platforms
            }
        })
    }, [data]);

    // Define our columns. Memoize this so that it isn't re-created on every re-render.
    const tableCols = useMemo<Column<TableData>[]>(() => [
        {
            Header: 'Image',
            accessor: 'image',
            Cell: ({value}: { value?: string }) => {
                // If there is no image, show nothing.
                if (!value) return <></>;
                // If there is an image URL, render it.
                return <img alt={'Playlist art'} src={value} width={75}/>
            }
        },
        {
            Header: 'Name',
            accessor: 'title'
        },
        {
            Header: 'Artist',
            accessor: 'artist',
            Cell: ({value}: { value: string[] }) => {
                if (!Array.isArray(value)) {
                    return <>{value}</>
                } else {
                    return <>{value.join(', ')}</>
                }
            }
        },
        {
            Header: 'Actions',
            Cell: ({cell}: { cell: any }) => {
                const origData = cell.row.original as IAlbum;

                function navToAlbum() {
                    navigate('/a/' + origData.id)
                }

                return <>
                    <ButtonGroup>
                        <EditAlbumModal album={origData as IAlbum}/>
                        <Button onClick={navToAlbum}><i className={'bi bi-file-earmark-music'} title={'Manage tracks'}/></Button>
                        <Button onClick={() => setItemToDelete(origData.id)}><i className={'bi bi-trash'}
                                                                                title={'Delete'}/></Button>
                    </ButtonGroup>
                </>
            }
        }
    ], [navigate]);

    // Init the table
    const table = useTable({
        columns: tableCols,
        data: tableData,
    })

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
    } = table;

    // Drag / Drop Setup
    const [rows, setRows] = useState(table.rows);

    //Item Styles for when dragging
    const getItemStyle = (isDragging: any, draggableStyle: any) => ({
        userSelect: "none",
        display: isDragging ? "table" : "table-row",
        boxShadow: isDragging ? "0 0 5px rgba(0,0,0,0.2)" : "none",

        // styles we need to apply on draggables
        ...draggableStyle
    });


    // Reordering the result
    const reorder = (list: any, startIndex: any, endIndex: any) => {
        const result = list;
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    // Update view on drag end
    const onDragEnd = (result: any) => {
        console.log(rows);
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        const items = reorder(
            rows,
            result.source.index,
            result.destination.index
        );

        setRows(items);
    }
    return (
        <>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId='droppable'>
                    {(provided) => (
                        <table
                            className={'table table-striped table-albums'}
                            ref={provided.innerRef}
                            {...getTableProps()}
                        >
                            <thead>
                            {
                                headerGroups.map((group) => (
                                    <tr {...group.getHeaderGroupProps()}>
                                        <th className='move'></th>
                                        {
                                            group.headers.map((col) => (
                                                <th {...col.getHeaderProps()}>
                                                    {col.render('Header')}
                                                </th>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {
                                rows.map((r, index) => {
                                    prepareRow(r);
                                    return (
                                        <Draggable key={r.id} draggableId={r.id} index={index}>
                                            {(provided, snapshot) => (
                                                <tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...r.getRowProps()}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    )}
                                                >
                                                    <td
                                                        className='move'
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <i className="bi bi-grip-vertical"></i>
                                                    </td>
                                                    {r.cells.map((c) => {
                                                        return (
                                                            <td {...c.getCellProps()}>
                                                                {c.render('Cell')}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            )}
                                        </Draggable>
                                    )
                                })
                            }
                            {provided.placeholder}

                            </tbody>
                        </table>
                    )}
                </Droppable>
            </DragDropContext>
            {/* Delete modal */}
            <ConfirmModal
                body={<>Deleting an album means that it will no longer be visible or playable. <strong>All tracks that
                    are part of the album will also be deleted from the database, and removed from
                    playlists.</strong></>}
                show={!!itemToDelete}
                onClose={() => setItemToDelete('')}
                onCancel={() => setItemToDelete('')}
                onConfirm={doDelete}
                cooldownSecs={5}
            />
        </>
    )
}

export default AlbumsTable;